// src/hooks/useDownload.js
import { useState, useCallback } from "react";
import { trackDownload } from "../utils/api";
import { useAuth } from "../context/AuthContext";

// Maps UI size labels to Pexels src keys
const SIZE_KEY = {
  original: "original",
  large:    "large2x",
  medium:   "large",
  small:    "medium",
};

export const useDownload = () => {
  const { user }                  = useAuth();
  const [downloading, setDownloading] = useState({}); // { [photoId]: boolean }

  /**
   * Downloads a photo file and (if logged in) tracks it server-side.
   * Returns a Promise — callers can await it to show toast feedback.
   */
  const downloadPhoto = useCallback(async (photo, size = "original") => {
    const id  = photo.id;
    const url = photo.src[SIZE_KEY[size]] || photo.src.original;

    setDownloading(prev => ({ ...prev, [id]: true }));

    try {
      // Stream image as blob so browser triggers a real file download
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Network error: HTTP ${res.status}`);

      const blob    = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor  = Object.assign(document.createElement("a"), {
        href:     blobUrl,
        download: `halro-${id}-${size}.jpg`,
      });
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);

      // Track download server-side (fire-and-forget — never blocks UX)
      if (user) {
        trackDownload({
          photoId:      id,
          photographer: photo.photographer,
          thumbnail:    photo.src.tiny,
          size,
        }).catch(() => { /* silently ignore tracking failures */ });
      }
    } finally {
      // Always clear the spinner even on error
      setDownloading(prev => ({ ...prev, [id]: false }));
    }
    // Note: errors propagate to the caller so they can show a toast
  }, [user]);

  return { downloadPhoto, downloading };
};
